"use client";
import { useState, useEffect, useCallback } from "react";
import Pagination from "../../Common/Pagination";
import axiosInstance from "../../../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { API_ENDPOINTS } from "../../../constants/config";
import { ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";

function AddSubject({ onClose, onSuccess }) {
  const [subjectData, setSubjectData] = useState({
    name: "",
    code: "",
  });
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    try {
      setError(null);
      await axiosInstance.post(API_ENDPOINTS.subjects, subjectData);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.message || "Failed to add subject");
      console.error("Error adding subject:", error);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Add New Subject</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <Input
            value={subjectData.name}
            onChange={(e) =>
              setSubjectData((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder="Enter subject name"
          />
          <Input
            value={subjectData.code}
            onChange={(e) =>
              setSubjectData((prev) => ({ ...prev, code: e.target.value }))
            }
            placeholder="Enter subject code"
          />
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Add Subject</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditSubject({ subjectData, onClose, onSuccess }) {
  const [subject, setSubject] = useState({
    name: subjectData.name,
    code: subjectData.code,
  });
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    try {
      setError(null);
      await axiosInstance.patch(
        API_ENDPOINTS.subject(subjectData._id),
        subject
      );
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update subject");
      console.error("Error updating subject:", error);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Edit Subject</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <Input
            value={subject.name}
            onChange={(e) =>
              setSubject((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder="Enter subject name"
          />
          <Input
            value={subject.code}
            onChange={(e) =>
              setSubject((prev) => ({ ...prev, code: e.target.value }))
            }
            placeholder="Enter subject code"
          />
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Update Subject</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function SubjectList() {
  const [subjects, setSubjects] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
    }
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (field !== sortField) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />
    );
  };

  const getSortedSubjects = () => {
    if (!sortField) return subjects;

    return [...subjects].sort((a, b) => {
      let valueA = a[sortField];
      let valueB = b[sortField];

      // Special handling for createdAt
      if (sortField === "createdAt") {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
      }

      // String comparison for text fields
      if (typeof valueA === "string" && typeof valueB === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const fetchSubjects = useCallback(async () => {
    try {
      setError(null);
      const response = await axiosInstance.get(API_ENDPOINTS.subjects);
      setSubjects(response.data.data.subjects);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch subjects");
      console.error("Error fetching subjects:", error);
    }
  }, []);

  const handleDelete = async (subjectId) => {
    try {
      setError(null);
      await axiosInstance.delete(API_ENDPOINTS.subject(subjectId));
      fetchSubjects();
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete subject");
      console.error("Error deleting subject:", error);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <Button variant="outline" onClick={fetchSubjects}>
            Refresh List
          </Button>
          <Button onClick={() => setShowAddModal(true)}>Add New Subject</Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="font-semibold text-gray-600 cursor-pointer"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center">
                  Subject Name
                  {getSortIcon("name")}
                </div>
              </TableHead>
              <TableHead
                className="font-semibold text-gray-600 cursor-pointer"
                onClick={() => handleSort("code")}
              >
                <div className="flex items-center">
                  Subject Code
                  {getSortIcon("code")}
                </div>
              </TableHead>
              <TableHead
                className="font-semibold text-gray-600 cursor-pointer"
                onClick={() => handleSort("createdAt")}
              >
                <div className="flex items-center">
                  Created At
                  {getSortIcon("createdAt")}
                </div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {getSortedSubjects().slice((page - 1) * itemsPerPage, page * itemsPerPage).map((subject) => (
              <TableRow key={subject._id}>
                <TableCell>{subject.name}</TableCell>
                <TableCell>{subject.code}</TableCell>
                <TableCell>
                  {new Date(subject.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedSubject(subject);
                        setShowEditModal(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleDelete(subject._id)}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Pagination
          currentPage={page}
          totalItems={subjects.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setPage}
          onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setPage(1); }}
          itemLabel="subjects"
        />

        {showAddModal && (
          <AddSubject
            onClose={() => setShowAddModal(false)}
            onSuccess={() => {
              setShowAddModal(false);
              fetchSubjects();
            }}
          />
        )}
        {showEditModal && selectedSubject && (
          <EditSubject
            subjectData={selectedSubject}
            onClose={() => {
              setShowEditModal(false);
              setSelectedSubject(null);
            }}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedSubject(null);
              fetchSubjects();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
